import React, { useState, useEffect } from "react";
import { Container } from "./styles";
import { Button, Form, Input, Select, Spin, Upload } from "antd";
import { toast } from "react-toastify";
import ContentBlockEditor from "../../components/ContentBlockEditor";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useLanguage } from "../../hooks/LanguageContext";
import api from "../../services/api";
import { returnCatch } from "../../services/functions";

const { Option } = Select;

export default function InsightForm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [photo, setPhoto] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const { language } = useLanguage();
  const isEditing = !!id;

  useEffect(() => {
    if (id) {
      loadingScreen(id);
    } else {
      setLoading(false);
    }
  }, [id]);

  async function loadingScreen() {
    setLoading(true);
    await api
      .get(`/admin/insight/${id}`)
      .then((response) => {
        form.setFieldsValue({
          title: response.data.title,
          description: response.data.description,
          session: response.data.session,
          phase: response.data.phase,
        });
        setPhoto({ photo_url: response.data.photo_url });
      })
      .catch(({ response }) => {
        returnCatch(response);
      });
    setLoading(false);
  }

  async function handleSubmit(values) {
    if (!photo) {
      toast.warn("Preencha todos os campos e envie uma imagem de capa");
    } else {
      setLoadingButton(true);
      if (isEditing) {
        await editedInsight(values);
      } else {
        await createdInsight(values);
      }
      setLoadingButton(false);
    }
  }

  async function createdInsight(values) {
    const data = new FormData();

    data.append("title", values.title);
    data.append("description", values.description);
    data.append("phase", values.phase);
    data.append("session", values.session);
    data.append("language", language);
    if (photo.name) {
      data.append("file", photo, photo.name);
    }

    await api
      .post("/admin/insight", data)
      .then(async (response) => {
        toast.success("Dica editada com sucesso");
        navigate(`/insights/edit/${response.data.id}`);
      })
      .catch(({ response }) => {
        returnCatch(response);
      });
  }

  async function editedInsight(values) {
    const data = new FormData();

    data.append("title", values.title);
    data.append("description", values.description);
    data.append("phase", values.phase);
    data.append("session", values.session);
    data.append("language", language);
    if (photo.name) {
      data.append("file", photo, photo.name);
    }

    await api
      .put(`/admin/insight/${id}`, data)
      .then(async (response) => {
        toast.success("Dica editada com sucesso");
        navigate(`/insights/edit/${response.data.id}`);
      })
      .catch(({ response }) => {
        returnCatch(response);
      });
  }

  return (
    <Container>
      <div style={{ maxWidth: 800, width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 15,
            marginBottom: 20,
          }}
        >
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/insights")}
            shape="circle"
          />
          <h1 style={{ margin: 0 }}>
            {isEditing ? "Editar Dica" : "Nova Dica"}
          </h1>
        </div>
        {loading ? (
          <Spin />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{
              background: "#fff",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px 20px",
              }}
            >
              <Form.Item
                label="Imagem de Capa"
                style={{
                  width: "100%",
                  marginBottom: 0,
                }}
                required
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {photo && (
                    <img
                      draggable={false}
                      src={photo.photo_url}
                      alt="avatar"
                      style={{
                        width: 150,
                        borderRadius: 10,
                        height: 150,
                        marginRight: 15,
                      }}
                    />
                  )}
                  <Upload
                    name="avatar"
                    listType="picture-card"
                    className="avatar-uploader"
                    showUploadList={false}
                    maxCount={1}
                    onRemove={() => {
                      setPhoto(null);
                    }}
                    beforeUpload={(currentFile) => {
                      const isImage = currentFile.type.startsWith("image/");
                      if (!isImage) {
                        toast.error(
                          "Você só pode carregar arquivos de imagem!",
                        );
                        return Upload.LIST_IGNORE;
                      }

                      currentFile.photo_url = URL.createObjectURL(currentFile);
                      setPhoto(currentFile);

                      console.log(currentFile);
                      return false;
                    }}
                    fileList={photo ? [photo] : []}
                  >
                    <div
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>
                        {photo ? "Editar" : "Adicionar"}
                      </div>
                    </div>
                  </Upload>
                </div>
              </Form.Item>

              <Form.Item
                name="title"
                label="Título"
                rules={[{ required: true, message: "Insira o título" }]}
                style={{
                  marginBottom: 0,
                  width: "100%",
                }}
              >
                <Input
                  placeholder="Ex: Descubra seu corpo"
                  size="large"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name="session"
                label="Sessão"
                rules={[{ required: true, message: "Insira a sessão" }]}
                style={{
                  marginBottom: 0,
                  alignSelf: "center",
                  flex: 1,
                }}
              >
                <Select
                  placeholder="Ex: Corpo"
                  size="large"
                  style={{ minWidth: 200 }}
                >
                  <Option value="Corpo">Corpo</Option>
                  <Option value="Emoções">Emoções</Option>
                  <Option value="Sono">Sono</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="phase"
                label="Fase do ciclo"
                rules={[{ required: true, message: "Insira a fase do ciclo" }]}
                style={{
                  marginBottom: 0,
                  alignSelf: "center",
                  flex: 1,
                }}
              >
                <Select
                  placeholder="Ex: Menstrual"
                  size="large"
                  style={{ minWidth: 200 }}
                >
                  <Option value="MENSTRUAL">Menstrual</Option>
                  <Option value="FOLICULAR">Folicular</Option>
                  <Option value="OVULATORIA">Ovulátoria</Option>
                  <Option value="LUTEA">Lutea</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="description"
                label="Descrição"
                rules={[{ required: true, message: "Insira a descrição" }]}
                style={{
                  marginBottom: 0,
                  alignSelf: "center",
                  width: "100%",
                }}
              >
                <Input.TextArea
                  placeholder="Ex: Com os seus hormonios a flor da pele"
                  size="large"
                  style={{ width: "100%", minHeight: 160 }}
                />
              </Form.Item>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 40,
                gap: 15,
              }}
            >
              <Button size="large" onClick={() => navigate("/insights")}>
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loadingButton}
                size="large"
              >
                {isEditing ? "Salvar Alterações" : "Criar Dica"}
              </Button>
            </div>
          </Form>
        )}
      </div>
    </Container>
  );
}
